import { globals } from 'config/globals';
import Config from 'config/settings';
import DelayHelper from 'utils/timers';
import { Simple } from 'utils/analytics';
import HomeScreens from 'app/views/home/webgl/particles/scenes/HomeScreens';
import ParticleHitTests from 'app/views/home/targets/ParticleHitTests';
import {
	BEHAVIOUR_BREATHING,
	BEHAVIOUR_DISABLE_TIMELINE_X,
	FLAG_ACTIVE
} from 'app/views/home/webgl/particlesOptimised/core/ParticleCore';
import { PhaseRunner } from 'app/views/home/webgl/particlesOptimised/departments/PhaseRunner';
import { GROUP_SCREENS, ScreensContext } from 'app/views/home/webgl/particlesOptimised/departments/screens/ScreensContext';
import { createScreensPhaseEntries } from 'app/views/home/webgl/particlesOptimised/departments/screens/screensPhaseEntries';

import type { ParticleCore } from 'app/views/home/webgl/particlesOptimised/core/ParticleCore';
import type { PhaseEntry } from 'app/views/home/webgl/particlesOptimised/departments/phases.types';
import type { DepartmentDeps } from 'app/views/home/webgl/particlesOptimised/departments/departments.types';
import type { DepartmentScene } from 'app/views/home/webgl/webgl.types';

/**
 * ScreensDepartment (particles-optimised) — FORK of
 * webgl/particles/departments/ScreensDepartment.ts + ScreensParticleProcessor
 * + RadialHalftoneImage (see screens/ScreensHalftone.ts), re-expressed as
 * lane writes over the screens pool and RESTRUCTURED onto the phase
 * framework (phases.types.ts / PhaseRunner): the reference's string-keyed
 * stage machine (_sectionsOrder + _nextScreensSection if-chain) became the
 * ordered phase list in screens/screensPhaseEntries.ts (THE place to
 * reorder/re-time/add phases), one file per section under
 * departments/screens/phases/. Phase BODIES are the verbatim stage-5 lane
 * ports — the retrofit changed structure, not math. HomeScreens (the TV
 * mask/darken/overlay singleton), ComboCounter, DelayHelper,
 * ParticleHitTests, HalftonePersonPixelCache and ImagePixelDataGrid are
 * REUSED as-is.
 *
 * The LIVE cycle (reference _sectionsOrder, timings verbatim — the screens
 * durations are per-pass FUNCTIONS, see the entries file):
 *   radialHalftoneImage 9s -> dropMachine 8.8s -> swirl 7s (all *0.55 on the
 *   first pass after a scene start).
 *
 * Structure mapping (reference -> here):
 *  - the microShapes / whoozyImage / pixelElephant / ttBallon phase methods
 *    exist in the reference but are UNREACHABLE (never listed in
 *    _sectionsOrder) — dead code, documented-not-ported;
 *  - ScreensParticleProcessor.initScreensShape's live prefix (everything
 *    before its early return): position.set(0, 0.45) gated on sizeReset,
 *    disableTimelineX = true, repell.target = screensMouse (the facade's
 *    per-pool repell centre supplies screensMouse — mouse minus the screens
 *    container x/y this class maintains);
 *  - the runner hooks = the reference _nextScreensSection preamble/
 *    postamble: ParticleHitTests off + the next-phase analytics
 *    (onTransition), the reduce-first-pass flag clear (onStarted — after
 *    the entries' frame functions AND the halftone's combo timings read it),
 *    the full-pass analytics (onCycleComplete). NO script-group cancel at
 *    transitions — unlike spaces, every screens script fires inside its own
 *    phase's duration in the reference (groups clear on stop/park only).
 *    There is no post-arm colour cycle (screens phases own their colours via
 *    globals.colourPhase — nothing here consults PhaseEntry.ownsColours);
 *  - the shared ComboCounter and reduce-first-pass flag live on the
 *    ScreensContext (the swirl deliberately stacks combo entries onto
 *    dropMachine's leftovers — see the context header);
 *  - ParticleBehaviourLookup.pause()/unpause() around the start beat ->
 *    deps.setKernelsPaused (facade skips kernels/scripts/uploads);
 *  - startAsActiveScene's _frameBG alpha tween is dead in the reference (the
 *    sprite is never added to any container) — not ported;
 *  - tick's testVisible loop is dead (testVisible body starts with `return`)
 *    — not ported;
 *  - _containerOffsets are only animated by the dead ttBallon phase — the
 *    live phases always run offsets {x: 0, y: 0, rotation: 0} (reset in
 *    _nextScreensSection), so the container position uses constants here;
 *  - the reference's `pixelData` setter is an empty body — not ported.
 *
 * Framework-vs-reference timing notes (analysed, no observable difference —
 * the same calls the spaces retrofit made):
 *  - the reference advance compares `counter > frames` where the runner uses
 *    `>=` — a measure-zero boundary difference on a fractional deltaTime
 *    accumulator;
 *  - the reference's _shapeFrameCounter accumulates from _active (scene
 *    start) while the first section only arms on the 40ms DelayHelper — the
 *    ~2.4 frames accrued in that window shorten the first phase; replicated
 *    via _preArmFrames -> runner.setCounter at the arm beat.
 */
export default class ScreensDepartment implements DepartmentScene {
	private _deps: DepartmentDeps;
	private _core: ParticleCore;
	private _ctx: ScreensContext;
	private _runner: PhaseRunner;

	private _active: boolean = false;
	//Counter frames accrued between scene start and the 40ms arm beat (the
	//reference's non-reset _shapeFrameCounter — see the class header):
	private _armed: boolean = false;
	private _preArmFrames: number = 0;

	//The ordered phase list + reference timings (see screensPhaseEntries.ts —
	//THE place to reorder/re-time/add screens phases):
	private _entries: Array<PhaseEntry>;

	private _resizeTimeout: ReturnType<typeof setTimeout> = null;

	constructor(deps: DepartmentDeps) {
		this._deps = deps;
		this._core = deps.core;
		this._ctx = new ScreensContext(deps.core, deps);

		//Reference constructor globals write (mobile layout flag):
		globals.screensMobileLayout = false;
		if (window.innerWidth < Config.mobileBreakpoint) {
			globals.screensMobileLayout = true;
		}

		//(The reference also builds an unused 100x100 Graphics and the dead
		//_frameBG sprite here — not ported.)

		this._entries = createScreensPhaseEntries(this._ctx);

		this._runner = new PhaseRunner(this._entries, {
			//_nextScreensSection preamble: hit tests off + the next-phase
			//analytics (the halftone-instance null lives in that phase's
			//hide(); container offsets are constants — see the class header):
			onTransition: (index, phase) => {
				ParticleHitTests.active = false;

				Simple.event('home_screens_next_phase', { phase: phase.name });
			},
			//_nextScreensSection postamble: the reduce-first-pass flag clears
			//AFTER the arm consumed it (duration + halftone combo timings):
			onStarted: () => {
				this._ctx.reduceFirstPass = false;
			},
			//Reference: _totalPasses++ + the event when index 0 re-arms with
			//the first pass behind it (the runner's wrap — never the initial
			//startPhase(0)); fires BEFORE the next_phase event, same order:
			onCycleComplete: (totalPasses) => {
				Simple.event('home_screens_full_phase_complete', { total_passes: totalPasses });
			}
		});
		this._runner.initAll();
	}

	//DepartmentScene surface
	//-----------------------------------------------------------------------------------------------

	public tick(): void {
		if (!this._active) return;

		this._positionParticleContainer();

		//The stage counter/advance (reference _counter), pre-arm accrual
		//before the 40ms beat (reference _shapeFrameCounter — see header):
		if (this._armed) {
			this._runner.tick(this._deps.context.deltaTime);
		} else {
			this._preArmFrames += this._deps.context.deltaTime;
		}

		this._ctx.counters.tick();

		//Glue the stage's screens-mask-image sprite to the timeline's frame
		//sprite (globals.framesprite crosses the seam — the timeline writes it
		//a beat after create(), hence the guard):
		const ttt = this._deps.stage.ttt;

		if (globals.framesprite) {
			ttt.x = globals.framesprite.x + 3;
			ttt.y = globals.framesprite.y;
			ttt.scale.y = globals.framesprite.scale.y;
			ttt.scale.x = globals.framesprite.scale.x;
		}

		//The current phase's own per-frame work (the halftone's staggered
		//point creation) — after the frame plumbing, framework contract:
		if (this._armed) {
			this._runner.phase.tick();
		}

		//(testVisible loop: dead in the reference. Micro shapes: dead phase.)
	}

	public startAsActiveScene(): void {
		this._active = true;
		this._ctx.reduceFirstPass = true;
		this._startState();

		//ParticleBehaviourLookup.pause() + the 170ms unpause / 210ms reveal
		//(native setTimeouts in the reference too):
		this._deps.setKernelsPaused(true);

		setTimeout(() => {
			this._deps.setKernelsPaused(false);
		}, 170);

		setTimeout(() => {
			this._deps.stage.getSubContainer('screens').visible = true;
		}, 210);
	}

	public stopAsActiveScene(): void {
		DelayHelper.clearGroup(GROUP_SCREENS);
		this._deps.scripts.cancelGroup(GROUP_SCREENS);
		ParticleHitTests.active = false;

		HomeScreens.reset();
		this._active = false;
		this._armed = false;
		this._ctx.counters.clear();

		//hide() + reset() on the current phase (nulls the halftone instance —
		//the reference's _radialHalftoneImage = null on stop):
		this._runner.stop();

		//pool.deactivateAllBehaviours(false) + deactivate() + active = false:
		const core = this._core;
		for (let i = this._ctx.range.start; i < this._ctx.range.end; i++) {
			core.behaviourMask[i] &= BEHAVIOUR_BREATHING | BEHAVIOUR_DISABLE_TIMELINE_X;
			core.stateFlags[i] &= ~FLAG_ACTIVE;
		}

		this._deps.stage.getSubContainer('screens').visible = false;
	}

	public startSceneDrag(): void {}

	public endSceneDrag(): void {}

	/** Reference resize: full stop, then a 500ms-debounced restart. */
	public resize(): void {
		this.stopAsActiveScene();

		if (this._resizeTimeout) {
			clearTimeout(this._resizeTimeout);
		}

		this._resizeTimeout = setTimeout(() => {
			this.startAsActiveScene();
			this._resizeTimeout = null;
		}, 500);
	}

	/** Reference ScreensDepartment.setupForWork ("deeplink to work") — the
	 *  ONLY department with a setupForWork body: park the choreography
	 *  (timers cleared, _active off) without touching particle/container
	 *  state — the shared side hides the whole scene container. The runner is
	 *  deliberately NOT stopped: the reference leaves the section machine
	 *  where it parked (the halftone instance included) until showFromWork's
	 *  full stop + restart. The reference's DelayHelper.clearGroup('screens')
	 *  covers timers this fork split across DelayHelper and the ScriptList
	 *  under the same group name, so both are cleared. */
	public setupForWork(): void {
		this._active = false;

		DelayHelper.clearGroup(GROUP_SCREENS);
		this._deps.scripts.cancelGroup(GROUP_SCREENS);
		this._ctx.counters.clear();
	}

	/** Reference ScreensDepartment.showFromWork, verbatim shape: the leading
	 *  _active/visible writes are immediately superseded by
	 *  stopAsActiveScene — net effect is a full stop now and a full restart
	 *  20ms later. The 20ms DelayHelper is UNGROUPED like the reference (a
	 *  stop inside the window does not cancel it); the reference's
	 *  globals.skipNormalParticleUpdates writes are the object pipeline's
	 *  one-frame-skip hack — not ported (stage-4 convention). */
	public showFromWork(): void {
		this._active = true;
		this._deps.stage.getSubContainer('screens').visible = false;

		this.stopAsActiveScene();

		DelayHelper.setTimeout(() => {
			this.startAsActiveScene();
		}, 20);
	}

	/** DEAD in the reference (no callers — the live work re-entry is
	 *  showFromWork; the gsap container tween never runs). */
	public fromWorkToHome(): void {}

	/** Facade destroy() hook (no reference equivalent): clear the debounced
	 *  resize restart. The 170/210ms native start-beat timeouts stay
	 *  unclearable, exactly like the reference's. */
	public destroy(): void {
		if (this._resizeTimeout) {
			clearTimeout(this._resizeTimeout);
			this._resizeTimeout = null;
		}
		this._ctx.counters.clear();
	}

	//Private — frame plumbing
	//-----------------------------------------------------------------------------------------------

	/** _positionParticleContainer: the masked screens container tracks the
	 *  screens zone across the drag; the x/y land in the shared
	 *  particleContainers seam (MouseTarget('screens') / ParticleCords /
	 *  the physics _getRealX read them). Offsets are constants — only the
	 *  dead ttBallon phase animates them. */
	private _positionParticleContainer(): void {
		const sizes = this._deps.getSizes();
		const x = this._deps.context.containerX + sizes.zonePositions.screensParticles.cx * sizes.virtualWidth;

		const container = this._deps.stage.getSubContainer('screens');
		container.x = x;
		container.y = 0;
		container.rotation = 0;

		this._deps.sceneProps.mouse.particleContainersX.screens = x;
		this._deps.sceneProps.mouse.particleContainersY.screens = container.y;
	}

	//Private — scene start
	//-----------------------------------------------------------------------------------------------

	private _startState(): void {
		this._armed = false;
		this._preArmFrames = 0;
		this._setParticlesAtStart();

		//Reference: DelayHelper.setTimeout(..., 40) with NO group — a stop
		//inside the 40ms window does not cancel it (kept bug-compatible; the
		//stray section runs against a hidden, deactivating pool). The counter
		//takes the pre-arm accrual (reference _shapeFrameCounter is not reset
		//by _nextScreensSection — see the class header):
		DelayHelper.setTimeout(() => {
			this._runner.startPhase(0);
			this._runner.setCounter(this._preArmFrames);
			this._armed = true;
		}, 40);
	}

	/** _setParticlesAtStart: first quarter now, the rest 20ms later. */
	private _setParticlesAtStart(): void {
		const range = this._ctx.range;
		const share = Math.round(range.count / 4);

		for (let n = 0; n < share; n++) {
			this._setParticleAtStart(range.start + n);
		}

		DelayHelper.setTimeout(() => {
			for (let n = share; n < range.count; n++) {
				this._setParticleAtStart(range.start + n);
			}
		}, 20);
	}

	/** deactivateAllBehaviours() [position OFF] + initScreensShape(true,
	 *  false) [live prefix: disableTimelineX + screensMouse repell target —
	 *  the sizeReset position write is skipped] + activate(). */
	private _setParticleAtStart(lane: number): void {
		const core = this._core;
		core.behaviourMask[lane] &= BEHAVIOUR_BREATHING; //everything off, no base reactivation
		core.behaviourMask[lane] |= BEHAVIOUR_DISABLE_TIMELINE_X;
		core.stateFlags[lane] |= FLAG_ACTIVE;
	}
}
